// LabelPrintModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import {
  applyLabelIdentifierSource,
  countSelectedWithoutIdentifier,
  filterSelectedLabels,
  hasAutomaticIdentifierData,
  LABEL_IDENTIFIER_SOURCES,
} from "../../../utils/label-print-selection";

const DEFAULT_LOGO_URL =
  "https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/Logo%20Calins.png";

export default function LabelPrintModal({
  open,
  onClose,
  title = "Imprimir etiqueta",
  initialLabels = null,
  logoUrl = DEFAULT_LOGO_URL,
  defaultSize = "40.5x18", // "40.5x18" | "30x18"
}) {
  const [size, setSize] = useState(defaultSize);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [logoRotatedDataURL, setLogoRotatedDataURL] = useState(null);
  const [logoError, setLogoError] = useState("");
  const [identifierSource, setIdentifierSource] = useState(
    LABEL_IDENTIFIER_SOURCES.TAG
  );

  const { W, H } = useMemo(() => {
    if (size === "30x18") return { W: 30, H: 18 };
    return { W: 40.5, H: 18 };
  }, [size]);

  const compact = W <= 30.01;

  // ✅ limites por tamaño
  const ID_MAX = compact ? 10 : 13;

  // ✅ Margen inferior extra SOLO en 30mm (para cortar)
  const CUT_MARGIN_BOTTOM_MM = compact ? 3 : 0;
  const CONTENT_H = Math.max(0, H - CUT_MARGIN_BOTTOM_MM);

  // 🔧 MODIFICADO: Ahora acepta un tercer parámetro "doTrim" (por defecto true)
  // Si envías false, respeta los espacios del usuario.
  const safe = (v, fallback = "", doTrim = true) => {
    let s = (v ?? "").toString();
    if (doTrim) s = s.trim();
    return s.length ? s : fallback;
  };

  const onlyDigits = (s) => (s ?? "").toString().replace(/\D/g, "");

  const formatDateEU = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  const parseDateEU = (s) => {
    const m = (s || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const yy = Number(m[3]);
    const d = new Date(yy, mm, dd);
    if (Number.isNaN(d.getTime())) return null;
    if (d.getFullYear() !== yy || d.getMonth() !== mm || d.getDate() !== dd) return null;
    return d;
  };

  const addMonths = (date, months) => {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() < day) d.setDate(0);
    return d;
  };

  const addYears = (date, years) => addMonths(date, years * 12);

  const todayEU = () => formatDateEU(new Date());
  const todayPlus1YearEU = () => formatDateEU(addYears(new Date(), 1));

  const isLabelEmpty = (lbl) => {
    // Aquí seguimos usando trim por defecto para saber si está vacía
    const a = safe(lbl.cert);
    const b = safe(lbl.idEquipo);
    const c = safe(lbl.fechaCal);
    const d = safe(lbl.fechaVto);
    return !a && !b && !c && !d;
  };

  const truncateHard = (s, max) => {
    const t = (s ?? "").toString();
    if (!t) return "";
    if (t.length <= max) return t;
    return t.slice(0, Math.max(0, max - 1)) + "…";
  };

  const newEmptyLabel = () => ({
    _id: cryptoId(),
    selected: true,
    cert: "",
    idEquipo: "",
    fechaCal: todayEU(),
    fechaVto: todayPlus1YearEU(),
  });

  const normalizeIncomingLabels = (arr) => {
    const src = Array.isArray(arr) ? arr : [];
    if (!src.length) return [newEmptyLabel()];

    const tCal = todayEU();
    const tVto = todayPlus1YearEU();

    return src.map((l) => {
      const normalized = {
        _id: l?._id || cryptoId(),
        selected: l?.selected !== false,
        cert: (l?.cert ?? "").toString(),
        idEquipo: (l?.idEquipo ?? "").toString(),
        fechaCal: safe(l?.fechaCal) || tCal,
        fechaVto: safe(l?.fechaVto) || tVto,
      };

      if (hasAutomaticIdentifierData(l)) {
        normalized._usesAutomaticIdentifier = true;
        normalized.sourceTag = (l?.sourceTag ?? l?.tag ?? "").toString();
        normalized.sourceSerial = (l?.sourceSerial ?? l?.serial ?? "").toString();
        normalized.instrumentId = l?.instrumentId || null;
        normalized.instrumentLabel = (l?.instrumentLabel ?? "").toString();
      }

      return normalized;
    });
  };

  const [labels, setLabels] = useState(() => [newEmptyLabel()]);

  useEffect(() => {
    if (!open) return;
    setSize(defaultSize);
    setIdentifierSource(LABEL_IDENTIFIER_SOURCES.TAG);
    setLabels(
      applyLabelIdentifierSource(
        normalizeIncomingLabels(initialLabels),
        LABEL_IDENTIFIER_SOURCES.TAG
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLabels, defaultSize]);

  const addLabelRow = () => setLabels((prev) => [...prev, newEmptyLabel()]);

  const duplicateLast = () =>
    setLabels((prev) => {
      const last = prev[prev.length - 1];
      const fallback = newEmptyLabel();
      return [
        ...prev,
        {
          _id: cryptoId(),
          selected: true,
          cert: last?.cert ?? fallback.cert,
          idEquipo: last?.idEquipo ?? fallback.idEquipo,
          fechaCal: last?.fechaCal ?? fallback.fechaCal,
          fechaVto: last?.fechaVto ?? fallback.fechaVto,
          ...(hasAutomaticIdentifierData(last)
            ? {
                _usesAutomaticIdentifier: true,
                sourceTag: last?.sourceTag ?? "",
                sourceSerial: last?.sourceSerial ?? "",
                instrumentId: last?.instrumentId || null,
                instrumentLabel: last?.instrumentLabel || "",
              }
            : {}),
        },
      ];
    });

  const removeLabel = (id) => setLabels((prev) => prev.filter((l) => l._id !== id));

  const updateLabelField = (id, field, value) =>
    setLabels((prev) => prev.map((l) => (l._id === id ? { ...l, [field]: value } : l)));

  const updateIdentifierSource = (source) => {
    setIdentifierSource(source);
    setLabels((current) => applyLabelIdentifierSource(current, source));
  };

  const setAllSelected = (selected) =>
    setLabels((current) => current.map((label) => ({ ...label, selected })));

  const setVtoPreset = (id, preset) => {
    setLabels((prev) =>
      prev.map((l) => {
        if (l._id !== id) return l;
        const base = parseDateEU(l.fechaCal) || new Date();
        const newVto =
          preset === "6m" ? formatDateEU(addMonths(base, 6)) : formatDateEU(addYears(base, 1));
        const newCal = parseDateEU(l.fechaCal) ? l.fechaCal : formatDateEU(base);
        return { ...l, fechaCal: newCal, fechaVto: newVto };
      })
    );
  };

  const blobToDataURL = async (blob) =>
    await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const rotateDataURLMinus90HiRes = async (dataUrl, scale = 4) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const w = img.width * scale;
    const h = img.height * scale;

    canvas.width = h;
    canvas.height = w;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);

    ctx.drawImage(
      img,
      (-img.width * scale) / 2,
      (-img.height * scale) / 2,
      img.width * scale,
      img.height * scale
    );

    return canvas.toDataURL("image/png");
  };

  useEffect(() => {
    if (!open) return;

    let alive = true;

    const load = async () => {
      try {
        setLogoError("");
        setLoadingLogo(true);
        setLogoRotatedDataURL(null);

        const res = await fetch(logoUrl, { cache: "no-store" });
        if (!res.ok) throw new Error(`No se pudo descargar el logo (${res.status})`);

        const blob = await res.blob();
        const dataUrl = await blobToDataURL(blob);
        const rotated = await rotateDataURLMinus90HiRes(dataUrl, 4);

        if (!alive) return;
        setLogoRotatedDataURL(rotated);
      } catch (e) {
        if (!alive) return;
        setLogoError(e?.message || "No se pudo cargar el logo");
        setLogoRotatedDataURL(null);
      } finally {
        if (alive) setLoadingLogo(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [open, logoUrl]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const buildFileName = (printedLabels) => {
    if (printedLabels.length === 1) {
      const c = onlyDigits(printedLabels[0].cert) || "etiqueta";
      return `${c}.pdf`;
    }
    return `Etiquetas_${printedLabels.length}.pdf`;
  };

  const ellipsizeToWidth = (doc, text, maxWidth) => {
    const t = (text ?? "").toString();
    if (!t) return "";
    if (doc.getTextWidth(t) <= maxWidth) return t;
    const ell = "…";
    let s = t;
    while (s.length > 1 && doc.getTextWidth(s + ell) > maxWidth) s = s.slice(0, -1);
    return s.length ? s + ell : ell;
  };

  const drawTextFit = (doc, { text, x, y, maxWidth, fontStyle, baseSize, minSize }) => {
    const raw = (text ?? "").toString();
    doc.setFont("helvetica", fontStyle);

    let size = baseSize;
    while (size > minSize) {
      doc.setFontSize(size);
      if (doc.getTextWidth(raw) <= maxWidth) {
        doc.text(raw, x, y);
        return;
      }
      size -= 0.2;
    }

    doc.setFontSize(minSize);
    doc.text(ellipsizeToWidth(doc, raw, maxWidth), x, y);
  };

  const drawOneLabel = (doc, lbl) => {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, W, H, "F");

    const SAFE_L = compact ? 0 : 0.35; // mm
    const SAFE_R = compact ? 0 : 0.95; // mm

    const originX = SAFE_L;
    const rightLimit = W - SAFE_R;

    const logoColW = compact ? 5.6 : 7.0;
    const labelColW = compact ? 10.2 : 16.0;

    // IMPORTANT: todas las X salen desde originX
    const tableX = originX + logoColW;
    const splitX = originX + logoColW + labelColW;
    const pad = compact ? 0.7 : 0.8;

    // Logo
    if (logoRotatedDataURL) {
      try {
        const LOGO_SCALE = compact ? 0.70 : 0.86;
        const logoW = logoColW * LOGO_SCALE;
        const logoH = CONTENT_H * LOGO_SCALE;
        const logoX = originX + (logoColW - logoW) / 2;
        const logoY = (CONTENT_H - logoH) / 2;
        doc.addImage(logoRotatedDataURL, "PNG", logoX, logoY, logoW, logoH, undefined, "SLOW");
      } catch {
        doc.setTextColor(0);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(compact ? 8 : 9);
        doc.text("CALINS", originX + logoColW / 2, CONTENT_H / 2, { angle: 90, align: "center" });
      }
    } else {
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(compact ? 8 : 9);
      doc.text("CALINS", originX + logoColW / 2, CONTENT_H / 2, { angle: 90, align: "center" });
    }

    doc.setTextColor(0);

    const calKey = compact ? "Calib." : "F. Calib.";
    const vtoKey = compact ? "Vto." : "F. Vto.";

    // 🔧 Ajuste fino global
    const VALUE_SHIFT_MM = 2;

    const rows = [
      { key: "Nº Cert.", val: safe(lbl.cert, ""), i: 0, valStyle: "bold", mode: "columns" },
      {
        key: "ID:",
        // 🔧 CAMBIO: doTrim = false. Permite espacios para mover el texto manualmente.
        val: truncateHard(safe(lbl.idEquipo, "", false), ID_MAX),
        i: 1,
        valStyle: "normal",
        mode: "inline",
      },
      { key: calKey, val: safe(lbl.fechaCal, ""), i: 2, valStyle: "normal", mode: "columns" },
      { key: vtoKey, val: safe(lbl.fechaVto, ""), i: 3, valStyle: "normal", mode: "columns" },
    ];

    const lineStep = compact ? 3.05 : 3.35;

    // Centrado vertical del bloque
    const blockH = (rows.length - 1) * lineStep;
    const yStart = CONTENT_H / 2 - blockH / 2;

    const keySize = compact ? 7.0 : 8.6;
    const valSizeBold = compact ? 8.2 : 10.0;
    const valSizeNormal = compact ? 7.8 : 9.4;

    // ✅ max widths
    const keyMax = labelColW - pad * 2;
    const valMax = Math.max(0, rightLimit - (splitX + pad));

    const leftX = tableX + pad;
    const fullMax = Math.max(0, rightLimit - leftX);

    rows.forEach((r) => {
      const y = yStart + r.i * lineStep;

      if (r.mode === "inline") {
        // ID: Bold
        doc.setFont("helvetica", "bold");
        doc.setFontSize(valSizeBold);
        const keyText = r.key + " ";
        const keyW = doc.getTextWidth(keyText);
        doc.text(keyText, leftX, y);

        // Valor ID: Normal (con espacios respetados)
        const remainingW = fullMax - keyW;
        drawTextFit(doc, {
          text: r.val,
          x: leftX + keyW,
          y,
          maxWidth: remainingW,
          fontStyle: "normal",
          baseSize: valSizeNormal,
          minSize: compact ? 6.8 : 8.4,
        });
        return;
      }

      // MODO COLUMNS
      drawTextFit(doc, {
        text: r.key,
        x: tableX + pad,
        y,
        maxWidth: keyMax,
        fontStyle: "bold",
        baseSize: keySize,
        minSize: compact ? 6.2 : 7.4,
      });

      // 🔧 LÓGICA DE DESPLAZAMIENTO CONDICIONAL
      const isCert = r.key === "Nº Cert.";
      const currentShift = isCert ? 0 : VALUE_SHIFT_MM;

      drawTextFit(doc, {
        text: r.val,
        x: splitX + pad - currentShift,
        y,
        maxWidth: valMax + currentShift,
        fontStyle: r.valStyle,
        baseSize: r.valStyle === "bold" ? valSizeBold : valSizeNormal,
        minSize: compact ? 6.8 : 8.4,
      });
    });
  };

  const generateMultiPDF = () => {
    const toPrint = filterSelectedLabels(labels).filter((l) => !isLabelEmpty(l));
    if (toPrint.length === 0) {
      window.alert("No hay etiquetas para imprimir. Agregá al menos una con datos.");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [W, H],
      compress: false,
      putOnlyUsedFonts: true,
      floatPrecision: 16,
    });

    toPrint.forEach((lbl, idx) => {
      if (idx > 0) doc.addPage([W, H], "landscape");
      drawOneLabel(doc, lbl);
    });

    const blob = doc.output("blob");
    downloadBlob(blob, buildFileName(toPrint));
  };

  if (!open) return null;

  const selectedLabels = filterSelectedLabels(labels);
  const selectedCount = selectedLabels.length;
  const printableCount = selectedLabels.filter((l) => !isLabelEmpty(l)).length || 0;
  const missingIdentifierCount = countSelectedWithoutIdentifier(labels);
  const hasAutomaticLabels = labels.some(hasAutomaticIdentifierData);
  const allSelected = labels.length > 0 && selectedCount === labels.length;

  // Preview (px) estable
  const PX_PER_MM = 10;
  const previewWpx = Math.round(W * PX_PER_MM);
  const previewHpx = Math.round(H * PX_PER_MM);
  const previewContentHpx = Math.round(CONTENT_H * PX_PER_MM);

  const firstForPreview =
    selectedLabels.find((l) => !isLabelEmpty(l)) || selectedLabels[0] || labels[0];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-3"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-3xl rounded-lg border border-white/10 bg-slate-950 p-4 text-slate-100 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-xs text-slate-400 mt-1">
              Se generará un PDF con <b>1 etiqueta por página</b>. Para imprimir:
              <b> Escala 100% / Tamaño real</b> (sin “Ajustar”).
            </p>
            {compact && (
              <p className="text-xs text-slate-400 mt-1">
                En <b>30×18</b>: se deja <b>{CUT_MARGIN_BOTTOM_MM}mm abajo</b> para corte.
              </p>
            )}
          </div>
          <button
            className="rounded-md border border-white/15 px-2 py-1 text-sm hover:bg-white/5"
            onClick={onClose}
          >
            Cerrar ✕
          </button>
        </div>

        {/* ✅ Contenedor con scroll para evitar que el modal se salga de la pantalla */}
        <div className="mt-3 max-h-[calc(85vh-80px)] overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-300">Tamaño:</span>
          <select
            className="rounded-md border border-white/15 bg-slate-900 px-2 py-1 text-sm"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          >
            <option value="40.5x18">40,5 × 18 mm</option>
            <option value="30x18">30 × 18 mm (margen inferior 3 mm)</option>
          </select>

          <span className="ml-auto text-xs text-slate-400">
            Logo: {loadingLogo ? "cargando…" : logoRotatedDataURL ? "ok" : "fallback"}
          </span>
        </div>

        {logoError && (
          <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            Logo: {logoError}. Se imprimirá “CALINS” como fallback.
          </div>
        )}

        {hasAutomaticLabels && (
          <div className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/10 p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="text-sm font-medium text-sky-100" htmlFor="label-identifier-source">
                Dato para identificar el equipo:
              </label>
              <select
                id="label-identifier-source"
                className="rounded-md border border-sky-400/30 bg-slate-900 px-3 py-1.5 text-sm text-white"
                value={identifierSource}
                onChange={(e) => updateIdentifierSource(e.target.value)}
              >
                <option value={LABEL_IDENTIFIER_SOURCES.TAG}>TAG</option>
                <option value={LABEL_IDENTIFIER_SOURCES.SERIAL}>Número de serie</option>
              </select>
              <span className="text-xs text-sky-100/70">
                Se aplica automáticamente a todas las etiquetas de esta orden.
              </span>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            className="rounded-md border border-white/15 px-3 py-1.5 hover:bg-white/5"
            onClick={() => setAllSelected(true)}
          >
            Seleccionar todas
          </button>
          <button
            type="button"
            className="rounded-md border border-white/15 px-3 py-1.5 hover:bg-white/5"
            onClick={() => setAllSelected(false)}
          >
            Quitar selección
          </button>
          <span className="text-slate-400">
            {selectedCount} de {labels.length} seleccionadas
          </span>
        </div>

        {missingIdentifierCount > 0 && (
          <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            {missingIdentifierCount} etiqueta{missingIdentifierCount === 1 ? "" : "s"} seleccionada{missingIdentifierCount === 1 ? "" : "s"} no tiene{missingIdentifierCount === 1 ? "" : "n"} {identifierSource === LABEL_IDENTIFIER_SOURCES.SERIAL ? "número de serie" : "TAG"}. Podés completar el campo ID manualmente o quitar su selección.
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            className="sm:w-[280px] rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={generateMultiPDF}
            disabled={printableCount === 0}
          >
            ⬇️ Generar y descargar PDF ({printableCount} etiquetas)
          </button>

          <button
            className="rounded-md border border-white/15 px-3 py-2 text-sm hover:bg-white/5"
            onClick={addLabelRow}
          >
            ➕ Agregar etiqueta
          </button>

          <button
            className="rounded-md border border-white/15 px-3 py-2 text-sm hover:bg-white/5"
            onClick={duplicateLast}
            title="Duplica la última etiqueta para cargar más rápido"
          >
            📋 Duplicar última
          </button>

          <div className="sm:ml-auto flex gap-2">
            <button
              className="rounded-md border border-white/15 px-3 py-2 text-sm hover:bg-white/5"
              onClick={() => setLabels([newEmptyLabel()])}
              title="Deja una sola etiqueta en blanco"
            >
              Limpiar
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-white/10 overflow-hidden">
          <div className="grid grid-cols-12 gap-0 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-300">
            <div className="col-span-1 flex items-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => setAllSelected(e.target.checked)}
                aria-label="Seleccionar todas las etiquetas"
              />
            </div>
            <div className="col-span-2">Cert.</div>
            <div className="col-span-3">ID</div>
            <div className="col-span-2">{compact ? "Calib." : "F. Calib."}</div>
            <div className="col-span-2">{compact ? "Vto." : "F. Vto."}</div>
            <div className="col-span-2 text-right"> </div>
          </div>

          <div className="divide-y divide-white/10">
            {labels.map((l, idx) => {
              const empty = isLabelEmpty(l);
              return (
                <div
                  key={l._id}
                  className={`grid grid-cols-12 gap-2 px-3 py-2 ${
                    l.selected === false || empty ? "opacity-60" : ""
                  }`}
                >
                  <div className="col-span-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={l.selected !== false}
                      onChange={(e) => updateLabelField(l._id, "selected", e.target.checked)}
                      aria-label={`Seleccionar etiqueta ${idx + 1}`}
                    />
                  </div>
                  <input
                    className="col-span-2 rounded-md border border-white/15 bg-slate-900 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-500"
                    placeholder="6112005"
                    value={l.cert}
                    onChange={(e) => updateLabelField(l._id, "cert", e.target.value)}
                  />

                  <input
                    className="col-span-3 rounded-md border border-white/15 bg-slate-900 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-500"
                    placeholder={compact ? "1234567890" : "1234567890123"}
                    value={l.idEquipo}
                    maxLength={ID_MAX}
                    onChange={(e) => updateLabelField(l._id, "idEquipo", e.target.value)}
                  />

                  <input
                    className="col-span-2 rounded-md border border-white/15 bg-slate-900 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-500"
                    placeholder={todayEU()}
                    value={l.fechaCal}
                    onChange={(e) => updateLabelField(l._id, "fechaCal", e.target.value)}
                  />
                  <input
                    className="col-span-2 rounded-md border border-white/15 bg-slate-900 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-500"
                    placeholder={todayPlus1YearEU()}
                    value={l.fechaVto}
                    onChange={(e) => updateLabelField(l._id, "fechaVto", e.target.value)}
                  />

                  <div className="col-span-2 flex items-center justify-end">
                    <button
                      className="rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/5 disabled:opacity-40"
                      onClick={() => removeLabel(l._id)}
                      disabled={labels.length <= 1}
                      title="Eliminar esta etiqueta"
                    >
                      🗑️
                    </button>
                  </div>

                  <div className="col-span-12 -mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                    <span>Etiqueta #{idx + 1}</span>
                    {l.instrumentLabel && <span>· {l.instrumentLabel}</span>}
                    {hasAutomaticIdentifierData(l) && (
                      <span>
                        · TAG: {safe(l.sourceTag, "—")} · Serie: {safe(l.sourceSerial, "—")}
                      </span>
                    )}
                    {l.selected === false && <span className="text-amber-300">(no se imprimirá)</span>}
                    {empty ? <span className="text-slate-500">(vacía — no se imprimirá)</span> : null}
                    <span className="ml-auto">Vto rápido:</span>
                    <button
                      className="rounded border border-white/15 px-2 py-[2px] hover:bg-white/5"
                      onClick={() => setVtoPreset(l._id, "1y")}
                    >
                      1 año
                    </button>
                    <button
                      className="rounded border border-white/15 px-2 py-[2px] hover:bg-white/5"
                      onClick={() => setVtoPreset(l._id, "6m")}
                    >
                      6 meses
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PREVIEW */}
        <div className="mt-4 rounded-md border border-white/10 bg-white p-3 text-black">
          <div className="text-[11px] text-slate-600 mb-2">
            Vista previa (referencia) de la primera etiqueta seleccionada:
          </div>

          <div
            style={{
              width: `${previewWpx}px`,
              height: `${previewHpx}px`,
              position: "relative",
              overflow: "hidden",
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: `${previewWpx}px`,
                height: `${previewContentHpx}px`,
                display: "flex",
              }}
            >
              <div
                style={{
                  width: `${Math.round((compact ? 5.6 : 7.0) * PX_PER_MM)}px`,
                  height: `${previewContentHpx}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {logoRotatedDataURL ? (
                  <img
                    src={logoRotatedDataURL}
                    alt="Logo"
                    style={{ width: "86%", height: "86%", objectFit: "contain" }}
                  />
                ) : (
                  <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontWeight: 900 }}>
                    CALINS
                  </div>
                )}
              </div>

              <div style={{ flex: 1, padding: "10px 10px 0 10px" }}>
                <div style={{ fontWeight: 900, fontSize: compact ? 18 : 22, lineHeight: 1.05 }}>
                  Nº Cert.&nbsp;
                  <span style={{ fontWeight: 900 }}>{safe(firstForPreview?.cert, "—")}</span>
                </div>

                <div style={{ fontWeight: 900, fontSize: compact ? 18 : 22, lineHeight: 1.05, marginTop: 10 }}>
                  ID:&nbsp;
                  <span style={{ fontWeight: 500 }}>
                    {truncateHard(safe(firstForPreview?.idEquipo, "—", false), ID_MAX)}
                  </span>
                </div>

                <div style={{ fontWeight: 900, fontSize: compact ? 18 : 22, lineHeight: 1.05, marginTop: 10 }}>
                  {compact ? "Calib." : "F. Calib."}&nbsp;
                  <span style={{ fontWeight: 500 }}>{safe(firstForPreview?.fechaCal, "—")}</span>
                </div>

                <div style={{ fontWeight: 900, fontSize: compact ? 18 : 22, lineHeight: 1.05, marginTop: 10 }}>
                  {compact ? "Vto." : "F. Vto."}&nbsp;
                  <span style={{ fontWeight: 500 }}>{safe(firstForPreview?.fechaVto, "—")}</span>
                </div>
              </div>
            </div>

            {CUT_MARGIN_BOTTOM_MM > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: 0,
                  width: `${previewWpx}px`,
                  height: `${Math.round(CUT_MARGIN_BOTTOM_MM * PX_PER_MM)}px`,
                  background: "#fff",
                }}
              />
            )}
          </div>

          <div className="mt-2 text-[11px] text-slate-600">
            Para imprimir: <b>Escala 100%</b>.
          </div>
        </div>
        </div>
        {/* ✅ Fin del contenedor con scroll */}
      </div>
    </div>
  );
}

function cryptoId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }
}
